import { makeStyles, tokens } from '@fluentui/react-components';
import { ArrowLeft24Regular, Open24Regular, People24Regular } from '@fluentui/react-icons';
import React, { useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProjectProvider, useProject } from '../context/ProjectContext';
import { getVisibleMissingEckdatenFields } from '../utils/eckdatenCompleteness';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        height: '100%',
    },
    sidebar: {
        width: '220px',
        minWidth: '220px',
        maxWidth: '220px',
        height: '100vh',
        overflowY: 'auto',
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground1,
        borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
        flexShrink: 0,
    },
    navItem: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusMedium,
        minHeight: '44px',
        width: '100%',
        border: 'none',
        backgroundColor: 'transparent',
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase400,
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    selectedNavItem: {
        backgroundColor: tokens.colorNeutralBackground3,
    },
    navItemIcon: {
        fontSize: '18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    navItemText: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase400,
        textAlign: 'left',
    },
    navItemContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: tokens.spacingHorizontalS,
    },
    navItemWithMissingData: {
        borderLeft: `3px solid ${tokens.colorPaletteRedBorder2}`,
        backgroundColor: tokens.colorPaletteRedBackground1,
    },
    navItemTextMissingData: {
        color: tokens.colorPaletteRedForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    missingDataBadge: {
        marginLeft: 'auto',
        flexShrink: 0,
        minWidth: '20px',
        height: '20px',
        padding: `0 ${tokens.spacingHorizontalXS}`,
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorPaletteRedBackground3,
        color: tokens.colorNeutralForegroundOnBrand,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: '20px',
        textAlign: 'center',
    },
    navSection: {
        marginTop: tokens.spacingVerticalL,
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    sectionLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: tokens.colorNeutralForeground3,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        textTransform: 'uppercase',
    },
    content: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
});

const ProjectLayoutContent: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id: string }>();
    const { project, userInfo } = useProject();

    const navigationItems = useMemo(() => {
        const allNavigationItems = [
            { label: 'Project Data', path: 'eckdaten' },
            { label: 'Project Team', path: 'mitarbeiter' },
            { label: 'Project Partners', path: 'eckdaten-partner' },
            { label: 'Documents', path: 'dokumente' },
            { label: 'Project Management', path: 'projektmanagement' },
            { label: 'Invoices', path: 'rechnungen' },
            { label: 'Risk Management', path: 'riskmanagement' },
            { label: 'Evaluations', path: 'evaluations' },
            { label: 'History', path: 'eckdaten-history' },
        ];

        return allNavigationItems.filter((item) => {
            // Only show Invoices if user is in privileged group
            if (item.path === 'rechnungen') {
                return userInfo?.isUserInAnyPrivilegedGroup ?? false;
            }
            return true;
        });
    }, [userInfo?.isUserInAnyPrivilegedGroup]);

    const missingEckdatenCount = useMemo(
        () => getVisibleMissingEckdatenFields(project, userInfo).length,
        [project, userInfo],
    );

    const externalLinks = [
        {
            label: 'Templates and Processes',
            url: 'https://imcfhkrems.sharepoint.com/sites/QM-Online/SitePages/Kernprozess-Forschung-&-Entwicklung.aspx?source=https%3A%2f%2fimcfhkrems.sharepoint.com%2fsites%2fQM-Online%2fSitePages%2fForms%2fByAuthor.aspx',
        },
    ];

    const handleNavigate = (path: string) => {
        navigate(`/project/${id}/${path}`);
    };

    const handleBackNavigation = () => {
        // Check where the user came from
        const navigationOrigin = sessionStorage.getItem('navigationOrigin');

        // Clear the flag after reading it
        sessionStorage.removeItem('navigationOrigin');

        // Navigate based on origin
        if (navigationOrigin === 'archive') {
            navigate('/archive');
        } else {
            // Default to dashboard
            navigate('/');
        }
    };

    const handleExternalLink = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const isPathActive = (path: string) => {
        const fullPath = `/project/${id}/${path}`;
        const projectRoot = `/project/${id}`;
        // For Eckdaten, also check if we're at the project root (/project/:id)
        if (path === 'eckdaten') {
            return location.pathname === fullPath || location.pathname === projectRoot;
        }
        return location.pathname === fullPath;
    };

    return (
        <div className={styles.container}>
            <div className={styles.sidebar}>
                <button
                    className={styles.navItem}
                    onClick={handleBackNavigation}
                    title="Back to Dashboard"
                >
                    <span className={styles.navItemIcon}>
                        <ArrowLeft24Regular />
                    </span>
                    <span className={styles.navItemText}>Back to Dashboard</span>
                </button>

                <div className={styles.navSection}>
                    <div className={styles.sectionLabel}>Project Navigation</div>
                    {navigationItems.map((item) => (
                        <button
                            key={item.path}
                            className={`${styles.navItem} ${isPathActive(item.path) ? styles.selectedNavItem : ''} ${item.path === 'eckdaten' && missingEckdatenCount > 0 ? styles.navItemWithMissingData : ''}`}
                            onClick={() => handleNavigate(item.path)}
                            title={item.label}
                        >
                            <span className={styles.navItemContainer}>
                                <span className={`${styles.navItemText} ${item.path === 'eckdaten' && missingEckdatenCount > 0 ? styles.navItemTextMissingData : ''}`}>{item.label}</span>
                                {item.path === 'eckdaten' && missingEckdatenCount > 0 && (
                                    <span
                                        className={styles.missingDataBadge}
                                        role="img"
                                        aria-label={`${missingEckdatenCount} required project fields not set`}
                                        title={`${missingEckdatenCount} required field(s) not set`}
                                    >
                                        {missingEckdatenCount}
                                    </span>
                                )}
                            </span>
                        </button>
                    ))}
                </div>

                {externalLinks.length > 0 && (
                    <div className={styles.navSection}>
                        <div className={styles.sectionLabel}>Resources</div>
                        {externalLinks.map((link) => (
                            <button
                                key={link.url}
                                className={styles.navItem}
                                onClick={() => handleExternalLink(link.url)}
                                title={link.label}
                            >
                                <span className={styles.navItemContainer}>
                                    <span className={styles.navItemIcon}>
                                        <Open24Regular />
                                    </span>
                                    <span className={styles.navItemText}>{link.label}</span>
                                </span>
                            </button>
                        ))}
                        {project && (
                            <button
                                className={`${styles.navItem} ${isPathActive('teams') ? styles.selectedNavItem : ''}`}
                                onClick={() => {
                                    if (project.teamsUrl) {
                                        window.open(project.teamsUrl, '_blank');
                                    } else {
                                        handleNavigate('teams');
                                    }
                                }}
                                title="Microsoft Teams"
                            >
                                <span className={styles.navItemContainer}>
                                    <span className={styles.navItemIcon}>
                                        <People24Regular />
                                    </span>
                                    <span className={styles.navItemText}>Teams</span>
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.content}>
                <Outlet />
            </div>
        </div>
    );
};

const ProjectLayout: React.FC = () => {
    return (
        <ProjectProvider>
            <ProjectLayoutContent />
        </ProjectProvider>
    );
};

export default ProjectLayout;
